import React, { useState } from 'react'
import { css } from '@emotion/css'
import { useIntl } from 'react-intl'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { Modal, ModalBody, ModalFooter } from '@contrast-security-inc/design-system-popovers'
import { TextInput } from '@contrast-security-inc/design-system-inputs'
import { useForm } from 'react-hook-form'
import { SendToEmailModalInputs, SendToEmailModalProps } from '../../types/modals'

export function SendToEmailModal({
  isOpen,
  onClose,
  title,
  sendItems,
  permittedDomains,
  validate
}: SendToEmailModalProps) {
  const { formatMessage } = useIntl()
  const [email, setEmail] = useState('')
  const {
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
    getFieldState
  } = useForm<SendToEmailModalInputs>()
  const fieldState = getFieldState('email')
  const isSubmitEnabled = fieldState.isDirty && !fieldState.error?.message

  const emailInputProps = register('email', {
    required: formatMessage({ id: 'validation_requiredEmail' }),
    pattern: {
      value:
        /^[_A-Za-z0-9'\-+]+(\.[+_A-Za-z0-9'-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*(\.[A-Za-z]{2,})$/,
      message: formatMessage({
        id: 'validation_enterValidEmail'
      })
    },
    validate: (email) => {
      const emailDomain = email && email.split('@')[1]

      if (
        permittedDomains &&
        permittedDomains.length > 0 &&
        permittedDomains.indexOf(emailDomain) === -1
      ) {
        return formatMessage({
          id: 'detail_vulnerabilities_enterEmailFromPermittedDomain'
        })
      }

      if (validate) return validate(email)

      return undefined
    }
  })

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = event.target.value
    setEmail(newEmail)
    await emailInputProps.onChange(event)
  }

  const handleClose = () => {
    setEmail('')
    reset()
    onClose()
  }

  return (
    <Modal variant="info" title={title} isOpen={isOpen} onClose={handleClose} size="sm">
      <form
        className={css`
          padding: 16px 20px;
        `}
        onSubmit={handleSubmit(() => {
          sendItems(getValues().email)
          handleClose()
        })}
      >
        <ModalBody>
          <TextInput
            {...emailInputProps}
            id="email"
            error={errors.email?.message}
            isRequired={emailInputProps.required}
            value={email}
            onChange={onChange}
            label={formatMessage({ id: 'title_email' })}
            placeholder={formatMessage({
              id: 'placeholder_enterEmailAddress'
            })}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            className={css`
              margin-left: 8px;
            `}
            onClick={handleClose}
          >
            {formatMessage({ id: 'action_cancel' })}
          </Button>
          <Button
            isDisabled={!isSubmitEnabled}
            className={css`
              margin-left: 8px;
            `}
            type="submit"
          >
            {formatMessage({ id: 'action_send' })}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
